import { Button, Col, Modal, notification, Row, Table, Tag, Space, Tooltip } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { coreApi } from "../../api/calls";
import parse from "html-react-parser";
import moment from "moment/moment";
import BuildingContactFilterPanel from "../Buildings/BuildingContactFilterPanel.jsx";
import PartnerAddCommunication from "../Buildings/PartnerAddCommunication";
import { CheckOutlined, CloseOutlined, EditOutlined, EyeTwoTone, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

/**
 * Page with all sent emails for Partner profile
 * @component
 * @alias PartnerCommunicationPane
 * @returns <React.Fragment /> with content
 */
const PartnerCommunicationPane = ({ partner, currentTabKey = '' }) => {
  // Translation
  const { t } = useTranslation();

  // Id from URL
  const { id } = useParams();

  // States
  const [emails, setEmails] = useState([]);
  const [email, setEmail] = useState({});
  const [pagination, setPagination] = useState({});
  const [params, setParams] = useState({});
  const [loading, setDataLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRowKey, setEditingRowKey] = useState(null);
  const [rawValue, setRawValue] = useState(null);
  const [filters, setFilters] = useState({ cases: ['phone', 'email', 'action'] });

  const fetchEmails = useCallback(() => {
    setDataLoading(true);

    const mergedParams = {
      ...params,
      ...filters,
    };

    coreApi
      .get(`/emails/partner/` + id, { params: mergedParams })
      .then((response) => {
        let { data, ...pagination } = response.data;

        setEmails(data);
        setPagination(pagination);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setDataLoading(false)
      });
  }, [params, filters]);

  const updateEmail = (record) => {
    setDataLoading(true);
    if(!record.building_id) {
      record.building_id = id;
    }
    coreApi
      .put("/emails/" + record.id, record)
      .catch((error) => {
        notification.error({ message: error?.response?.data?.message });
      }).finally(() => {
      setDataLoading(false)
    });
  };

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  useEffect(() => {
    if (currentTabKey === '#communication') {
      fetchEmails();
    }
    return () => {}
  }, [currentTabKey]);

  const handleSave = (record) => {
    if (record.body_html !== rawValue) {
      record.body_html = rawValue;
      updateEmail(record);
    }
    handleCancel();
  };

  const handleEdit = (record) => {
    setEditingRowKey(record.id)
    setRawValue(record.body_html || record.subject);
  };

  const isEditing = (record) => record.id === editingRowKey;

  const handleCancel = () => {
    setEditingRowKey(null);
    setRawValue(null);
  };

  function cleanText(input) {
    if (!input) return '';
    return input
      .replace(/(@media[\s\S]*?\{[\s\S]*?\})/g, "")
      .replace(/(\.\w+[\s\S]*?\{[\s\S]*?\})/g, "")
      .replace(/@[\s\S]*?\}/g, "")
      .replace(/^[\s\}]+/g, "")
      .replace(/\n\s*\n+/g, "\n")
      .trim();
  }

  /**
   * Converts order numbers in text to clickable links
   * @param {string} text - Text that may contain "Změna v objednávce" with order number
   * @returns {JSX.Element|string} - Text with order number as link or original text
   */
  const convertOrderNumberToLink = (text) => {
    if (!text || typeof text !== 'string') return text;

    // Check if text contains "Změna v objednávce"
    if (text.includes('Změna v objednávce')) {
      // Extract the first number (with leading zeros) from the text
      const numberMatch = text.match(/\d+/);
      
      if (numberMatch) {
        const fullNumber = numberMatch[0]; // Full number with leading zeros (e.g., "00123")
        const orderId = parseInt(fullNumber, 10); // Parse to integer for URL (e.g., 123)
        
        // Split text by the full number to insert link
        const parts = text.split(fullNumber);
        
        return (
          <span>
            {parts[0]}
            <Link to={`/orders/${orderId}`} target="_blank">
              {fullNumber}
            </Link>
            {parts.slice(1).join(fullNumber)}
          </span>
        );
      }
    }

    return text;
  };

  const isActionCaseWithException = (record) => {
    const exceptionTypes = ['cleaningDayChange', 'clientPartnerChange', 'clientSubstitute', 'cleaningTimeChange'];
    
    // Check if it's an action case
    const isActionCase = record.case === 'action' || 
      (record.tags && record.tags.some(tag => tag.type === 'communication_case' && tag.key === 'action'));
    
    if (!isActionCase) {
      return false;
    }

    // Check if it has an exception type
    const hasExceptionType = (record.type && exceptionTypes.includes(record.type)) ||
      (record.tags && record.tags.some(tag => tag.type === 'communication_type' && exceptionTypes.includes(tag.key)));

    // Return true only if it's action case WITHOUT exception type
    return !hasExceptionType;
  };

  const getTypeColor = (typeKey) => {
    if (['invoice', 'finance', 'reminder', 'contract', 'balance', 'document'].includes(typeKey)) {
      return 'green';
    }

    if (['other'].includes(typeKey)) {
      return 'default';
    }

    if (['operational', 'review', 'schedule', 'clientSchedule', 'partnerSchedule', 'partnerScheduleManual', 'info', 'substitute', 'partnerSubstitute', 'clientSubstitute', 'offerSubstitute', 'clientPartnerChange', 'cleaningDayChange', 'cleaningTimeChange', 'clientSubstitute'].includes(typeKey)) {
      return 'orange';
    }

    if (['verification', 'claim'].includes(typeKey)) {
      return 'red';
    }

    return 'default';
  };

  const isEditableRecord = (record) => {
    const communication_cases = ['phone', 'email'];
    const communications = ['other']; // Only 'other' type is editable for partners

    let hasValidCase = false;
    if (record.case && communication_cases.includes(record.case)) {
      hasValidCase = true;
    }
    if (!hasValidCase && record.tags && record.tags.length > 0) {
      const caseTags = record.tags.filter(tag => tag.type === 'communication_case');
      hasValidCase = caseTags.some(tag => communication_cases.includes(tag.key));
    }

    let hasValidType = false;
    if (record.type && communications.includes(record.type)) {
      hasValidType = true;
    }
    if (!hasValidType && record.tags && record.tags.length > 0) {
      const typeTags = record.tags.filter(tag => tag.type === 'communication_type');
      hasValidType = typeTags.some(tag => communications.includes(tag.key));
    }

    return hasValidCase && hasValidType;
  };

  const isDeletableRecord = (record) => {
    const communications = ['other'];

    let hasValidType = false;
    if (record.type && communications.includes(record.type)) {
      hasValidType = true;
    }
    if (!hasValidType && record.tags && record.tags.length > 0) {
      const typeTags = record.tags.filter(tag => tag.type === 'communication_type');
      hasValidType = typeTags.some(tag => communications.includes(tag.key));
    }

    return hasValidType;
  };

  const deleteCommunication = (record) => {
    if (!record.id) {
      notification.error({ message: t("common.error") + ": Missing record ID" });
      return;
    }

    Modal.confirm({
      title: t("emails.communication_delete_title"),
      content: t("emails.communication_delete_content"),
      icon: <ExclamationCircleOutlined />,
      okText: t("common.delete"),
      okButtonProps: { type: "danger" },
      onOk: () => {
        setDataLoading(true);
        coreApi
          .delete("/emails/" + record.id)
          .then(() => {
            notification.success({ message: t("common.recordDeleted") });
            fetchEmails();
          })
          .catch((error) => {
            notification.error({ message: error?.response?.data?.message || t("common.deletionFailed") });
          })
          .finally(() => {
            setDataLoading(false);
          });
      },
    });
  };

  // Columns of table - Partner profile has different structure than Building profile
  const columns = [
    {
      title: t("emails.date"),
      key: "created_at",
      dataIndex: "created_at",
      width: "8%",
      render: (rowData) => {
        return moment(rowData).format("DD") + "." + moment(rowData).format("MM") + "." + moment(rowData).format("YYYY");
      },
    },
    {
      title: t("emails.communication"),
      key: "case",
      width: "12%",
      render: (_, record) => {
        const allCaseTags = [];

        if (record.case && record.case !== 'null') {
          const translationKey = "emails.communication_cases." + record.case;
          const translatedText = t(translationKey);
          allCaseTags.push({
            id: `case-${record.case}`,
            key: record.case,
            name: translatedText !== translationKey ? translatedText : record.case,
            isOld: true
          });
        }

        if (record.tags && record.tags.length > 0) {
          const caseTags = record.tags.filter(tag => tag.type === 'communication_case');
          caseTags.forEach(tag => {

            if (tag.key && tag.key !== 'null' && !allCaseTags.some(t => t.key === tag.key)) {
              allCaseTags.push({
                id: tag.id,
                key: tag.key,
                name: tag.translation?.tag_name || tag.key,
                isOld: false
              });
            }
          });
        }

        if (allCaseTags.length === 0) {
          return <Tag>-</Tag>;
        }

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {allCaseTags.map((tag) => (
              <Tag key={tag.id} color="blue">
                {tag.name}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: t("emails.communication_type"),
      key: "type",
      width: "12%",
      render: (_, record) => {
        const allTypeTags = [];

        if (record.type && record.type !== 'null') {
          const translationKey = "emails.types." + record.type;
          const translatedText = t(translationKey);
          allTypeTags.push({
            id: `type-${record.type}`,
            key: record.type,
            name: translatedText !== translationKey ? translatedText : record.type,
            isOld: true
          });
        }

        if (record.tags && record.tags.length > 0) {
          const typeTags = record.tags.filter(tag => tag.type === 'communication_type');
          typeTags.forEach(tag => {
            if (tag.key && tag.key !== 'null' && !allTypeTags.some(t => t.key === tag.key)) {
              allTypeTags.push({
                id: tag.id,
                key: tag.key,
                name: tag.translation?.tag_name || tag.key,
                isOld: false
              });
            }
          });
        }

        if (allTypeTags.length === 0) {
          return <Tag>-</Tag>;
        }

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {allTypeTags.map((tag) => (
              <Tag key={tag.id} color={getTypeColor(tag.key)}>
                {tag.name}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: t("emails.building_address"),
      key: "building_address",
      width: "15%",
      render: (_, element) => {
        const isActionCase = element.case === 'action' || 
          (element.tags && element.tags.some(tag => tag.type === 'communication_case' && tag.key === 'action'));

        const exceptionKeys = ['info', 'offerPermanent', 'offerSubstitute'];
        const hasExceptionType = element.tags && element.tags.some(
          tag => tag.type === 'communication_type' && exceptionKeys.includes(tag.key)
        );

        if (isActionCase && hasExceptionType) {
          return "-";
        }

        if (element.building_unit_full) {
          return (
            <Link
              to={element.building_unit_full.link}
              key={element.building_unit_full.link}
              target="_blank"
            >
              {element.building_unit_full.title}
            </Link>
          );
        }

        if (element.building_full) {
          return (
            <Link
              to={element.building_full.link}
              key={element.building_full.link}
              target="_blank"
            >
              {element.building_full.title}
            </Link>
          );
        }

        return "-";
      },
    },
    {
      title: t("invoices.manager"),
      key: "from_full",
      dataIndex: "from_full",
      width: "12%",
      render: (rowData) => {
        return rowData ?? t("common.system");
      },
    },
    {
      title: t("emails.subject"),
      key: "subject",
      width: "18%",
      render: (rowData, record) => {
        // Check if record has "contract" type
        const hasContractType = (record.type === 'contract') ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && tag.key === 'contract'));

        const hasOtherType = (record.type === 'other') ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && tag.key === 'other'));

        if (hasContractType || hasOtherType) {
          return '-';
        }

        if (isActionCaseWithException(record)) {
          return record.subject || '-';
        }

        return record.subject || '-';
      },
    },
    {
      title: t("emails.description"),
      key: "description",
      dataIndex: "body_html",
      width: "20%",
      render: (rowData, record) => {
        // Check if record has "contract" type
        const hasContractType = (record.type === 'contract') ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && tag.key === 'contract'));

        const bodyHtmlTypes = ['other', 'review', 'verification', 'finance', 'operational'];
        const hasBodyHtmlType = (record.type && bodyHtmlTypes.includes(record.type)) ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && bodyHtmlTypes.includes(tag.key)));

        // Types that should not display body
        const noBodyTypes = [
          'invoice', 'document', 'reminder', 'clientSchedule', 'balance',
          'partnerBuildingSwitch', 'partnerSubstitute', 'offerPermanent',
          'offerSubstitute', 'partnerScheduleManual', 'partnerSchedule', 'info', 'schedule'
        ];

        const hasNoBodyType = (record.type && noBodyTypes.includes(record.type)) ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && noBodyTypes.includes(tag.key)));

        if (hasNoBodyType) {
          return '-';
        }

        let plainText = record.body_html ? record.body_html.replace(/<\/?[^>]+(>|$)/g, '') : '';
        let displayText = cleanText(record.description || plainText || record.subject || '');
        
        if (hasContractType) {
          return convertOrderNumberToLink(displayText) || '-';
        }

        if (hasBodyHtmlType) {
          return isEditing(record) ? (
            <div
              contentEditable
              suppressContentEditableWarning
              id={record.id}
              onInput={(e) => setRawValue(e.target.textContent)}
              style={{
                cursor: "text",
                border: "1px solid #1890ff",
              }}
            >
              {displayText}
            </div>
          ) : (
            convertOrderNumberToLink(displayText) || '-'
          );
        }

        if (isActionCaseWithException(record)) {
          return '-';
        }

        return isEditing(record) ? (
          <div
            contentEditable
            suppressContentEditableWarning
            id={record.id}
            onInput={(e) => setRawValue(e.target.textContent)}
            style={{
              cursor: "text",
              border: "1px solid #1890ff",
            }}
          >
            {displayText}
          </div>
        ) : (
          convertOrderNumberToLink(displayText) || '-'
        );
      },
    },
    {
      key: "action",
      width: "8%",
      render: (_, record) => {
        return isEditing(record)
          ? <Space>
            <Button icon={<CheckOutlined />} onClick={() => handleSave(record)} size="small" />
            <Button danger icon={<CloseOutlined />} onClick={handleCancel} size="small" />
          </Space>
          : <Space>
            <Tooltip title={t("emails.view_email")}>
              <Button
                icon={<EyeTwoTone />}
                size="small"
                onClick={() => {
                  setEmail(record);
                  setIsModalVisible(true);
                }}
              />
            </Tooltip>
            {isEditableRecord(record) ? (
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(record)}
              />
            ) : (
              <Tooltip title={t("emails.system_communication_not_editable")}>
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  disabled
                />
              </Tooltip>
            )}
            {isDeletableRecord(record) && (
              <Button
                icon={<DeleteOutlined />}
                danger
                size="small"
                onClick={() => deleteCommunication(record)}
              />
            )}
          </Space>;
      },
    }
  ];

  return (
    <React.Fragment>
      <Row>
        <Col span={24} gutter={[16, 16]} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <BuildingContactFilterPanel
            filters={filters}
            setFilters={setFilters}
            fetchEmails={fetchEmails}
            t={t}
            context="partner"
          />
          {partner && <PartnerAddCommunication id={partner.id} onUpdate={fetchEmails}></PartnerAddCommunication>}
        </Col>
      </Row>
      <Table
        id="communication_table"
        size={"small"}
        loading={loading}
        dataSource={emails}
        rowKey="id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          onChange: (e, perPage) => {
            setParams({ ...params, page: e, perPage: perPage });
          },
          showTotal: (total, range) => (
            <p>
              {t("common.paginationPartOne")} <strong>{range[0]}</strong>-<strong>{range[1]}</strong>
              {" " + t("common.paginationPartTwo") + " "}
              <strong>{total}</strong> {t("common.paginationPartThree")}
            </p>
          ),
        }}
        columns={columns}
        onChange={(e) => {
          console.log(e);
        }}
      />

      <Modal
        width={1000}
        open={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
      >
        {email.body_html?.length === 0 ? (
          email.subject
        ) : (
          email.body_html && parse(email.body_html)
        )}
      </Modal>
    </React.Fragment>
  );
};

export default PartnerCommunicationPane;
